import React, { useEffect, useState } from 'react';
import { Confirm } from 'semantic-ui-react';
import { useScheduleTree } from '../../providers/ScheduleTreeProvider';
import { MillerColumns } from './MillerColumns';
import { useDragAutoScroll } from './useDragAutoScroll';
import { componentLabel } from './componentLabel';

export const ScheduleTab = ({ onError }) => {
    const {
        sourceColumns,
        sourceSelectedPath,
        sourceLoadingColumnIndex,
        loadSourceRoots,
        selectSource,
        targetColumns,
        targetSelectedPath,
        targetLoadingColumnIndex,
        loadTargetRoots,
        selectTarget,
        draggedComponent,
        setDraggedComponent,
        draggedScheduleNode,
        setDraggedScheduleNode,
        addPending,
        addRoot,
        addChild,
        removeNode,
        moveNode,
    } = useScheduleTree();

    const [rootZoneActive, setRootZoneActive] = useState(false);
    const [pendingRemoval, setPendingRemoval] = useState(null);

    useDragAutoScroll(!!(draggedComponent || draggedScheduleNode));

    useEffect(() => {
        let cancelled = false;
        if (sourceColumns.length === 0) {
            Promise.resolve(loadSourceRoots()).catch(() => {
                if (!cancelled && onError) onError('Failed to load components.');
            });
        }
        if (targetColumns.length === 0) {
            Promise.resolve(loadTargetRoots()).catch(() => {
                if (!cancelled && onError) onError('Failed to load the schedule tree.');
            });
        }
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const serverMessage = (err, fallback) => {
        const data = err?.response?.data;
        const text =
            typeof data === 'string' ? data
            : data && typeof data === 'object' ? data.message || data.detail || data.title
            : null;
        if (!text || !text.trim() || text.trim().startsWith('<') || text.length > 300) return fallback;
        return text.trim();
    };

    const runWrite = (promise, fallback = 'Failed to update the schedule tree.') => {
        Promise.resolve(promise).catch(err => {
            if (onError) onError(serverMessage(err, fallback));
        });
    };

    const confirmRemoval = () => {
        const target = pendingRemoval;
        setPendingRemoval(null);
        if (target) runWrite(removeNode(target.item, target.columnIndex, target.parentId));
    };

    const targetRootColumn = targetColumns[0];
    const targetIsEmpty = targetRootColumn && (targetRootColumn.items?.length ?? 0) === 0;

    return (
        <div className="schedule-tab" data-testid="schedule-tab">
            <section className="schedule-strip" data-testid="schedule-source-strip">
                <h3 className="schedule-strip-label">Components</h3>
                <div className="component-manager-navigator">
                    <MillerColumns
                        columns={sourceColumns}
                        selectedPath={sourceSelectedPath}
                        onSelect={selectSource}
                        loadingColumnIndex={sourceLoadingColumnIndex}
                        expansionKey="has_dependents"
                        onError={() => onError && onError('Failed to load component children.')}
                        onItemDragStart={item => setDraggedComponent(item)}
                        onItemDragEnd={() => setDraggedComponent(null)}
                    />
                </div>
            </section>

            <section className="schedule-strip" data-testid="schedule-target-strip">
                <h3 className="schedule-strip-label">Schedule</h3>

                <div className="component-manager-navigator">
                    {targetIsEmpty ? (
                        <div
                            className={`component-manager-empty schedule-empty-dropzone${
                                rootZoneActive ? ' drag-over' : ''
                            }${addPending ? ' disabled' : ''}`}
                            data-testid="schedule-empty"
                            role="region"
                            aria-label="Drop a component here to add it as a schedule root"
                            onDragOver={e => {
                                e.preventDefault();
                                e.dataTransfer.dropEffect = addPending ? 'none' : 'copy';
                                if (!addPending) setRootZoneActive(true);
                            }}
                            onDragLeave={() => setRootZoneActive(false)}
                            onDrop={e => {
                                e.preventDefault();
                                setRootZoneActive(false);
                                if (addPending) return;
                                if (draggedScheduleNode) {
                                    runWrite(
                                        moveNode({
                                            childId: draggedScheduleNode.item.id,
                                            fromParentId: draggedScheduleNode.parentId,
                                            toParentId: null,
                                        })
                                    );
                                    return;
                                }
                                if (!draggedComponent) return;
                                runWrite(addRoot(draggedComponent));
                            }}
                        >
                            No schedule tree yet. Drag a component here to add a root.
                        </div>
                    ) : (
                        <MillerColumns
                            columns={targetColumns}
                            selectedPath={targetSelectedPath}
                            onSelect={selectTarget}
                            loadingColumnIndex={targetLoadingColumnIndex}
                            expansionKey="childCount"
                            dragActive={!!(draggedComponent || draggedScheduleNode)}
                            onError={() => onError && onError('Failed to load schedule children.')}
                            onItemDragStart={(item, columnIndex) =>
                                setDraggedScheduleNode({
                                    item,
                                    parentId: targetColumns[columnIndex]?.parentId ?? null,
                                })
                            }
                            onItemDragEnd={() => setDraggedScheduleNode(null)}
                            onNodeDrop={node => {
                                if (draggedScheduleNode) {
                                    runWrite(
                                        moveNode({
                                            childId: draggedScheduleNode.item.id,
                                            fromParentId: draggedScheduleNode.parentId,
                                            toParentId: node.id,
                                        })
                                    );
                                    return;
                                }
                                if (!draggedComponent) return;
                                runWrite(addChild(node, draggedComponent));
                            }}
                            onColumnDrop={columnIndex => {
                                const parentId = targetColumns[columnIndex]?.parentId ?? null;
                                if (draggedScheduleNode) {
                                    runWrite(
                                        moveNode({
                                            childId: draggedScheduleNode.item.id,
                                            fromParentId: draggedScheduleNode.parentId,
                                            toParentId: parentId,
                                        })
                                    );
                                    return;
                                }
                                if (!draggedComponent) return;
                                if (parentId == null) {
                                    runWrite(addRoot(draggedComponent));
                                    return;
                                }
                                const parentNode = targetColumns[columnIndex - 1]?.items.find(
                                    it => it.id === parentId
                                );
                                if (parentNode) runWrite(addChild(parentNode, draggedComponent));
                            }}
                            dropDisabled={addPending}
                            onItemRemove={(item, columnIndex) =>
                                setPendingRemoval({
                                    item,
                                    columnIndex,
                                    parentId: targetColumns[columnIndex]?.parentId ?? null,
                                })
                            }
                        />
                    )}
                </div>
            </section>

            <Confirm
                open={pendingRemoval != null}
                header="Remove from schedule"
                content={
                    pendingRemoval
                        ? `Remove "${componentLabel(pendingRemoval.item)}" from the schedule?`
                        : ''
                }
                confirmButton="Remove"
                onConfirm={confirmRemoval}
                onCancel={() => setPendingRemoval(null)}
                data-testid="schedule-remove-confirm"
            />
        </div>
    );
};
